import { useEffect, useRef, useState } from "react";
import "./AltarCard.scss";
import Card from "./Card";
import TgSticker from "../sticker/TgSticker";
import { STICKERS } from "../../assets/media/stickers";
import Button from "../button/Button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getCard } from "../../api/card";
import gsap from "gsap";
import { IMAGES } from "../../assets/media/images";
import { getCardImage } from "../../funcs/card";
import { UseNotification } from "../../context/notificationContext";
import InfoSvg from "../../assets/svg/InfoSvg";
import DialogWindow from "../modalWindow/DialogWindow";
import Weights from "./Weights";
import CardInfo from "./CardInfo";

function AltarCard() {
    const [currentCard, setCurrentCard] = useState(null);
    const [showCard, setShowCard] = useState(null);
    const [isOpenInfo, setIsOpenInfo] = useState(false);
    const [isRenderingInfo, setIsRenderingInfo] = useState(false);
    const { showNotification } = UseNotification();
    const cardRef = useRef(null);
    const descriptionRef = useRef(null);
    const timeline = useRef(null);
    const queryClient = useQueryClient();

    const { mutate: fetchCard } = useMutation({
        mutationFn: getCard,
        onError: (error) => handleError(error),
        onSuccess: (newCard) => {
            setCurrentCard(newCard);
            queryClient.invalidateQueries({queryKey: ["user"]});
        },
    });

    const onClickGetCard = () => {
        fetchCard();
    };

    const handleError = (error) => {
        if (error.response.data.error === "card timeout") {
            showNotification("Время ожидания следующей крутки ещё не прошло. Возвращайся позже!");
        } else {
            showNotification("Произошла ошибка. [" + error.response.data.error + "]");
        };
    };

    useEffect(() => {
        if (currentCard && cardRef.current) {
            timeline.current = gsap.timeline({
                defaults: {
                    duration: .3,
                    ease: "power2.out",
                }
            })
                .to(cardRef.current, {scale: .8, opacity: 0, onComplete: () => setShowCard(currentCard)})
                .to(cardRef.current, {scale: 1, opacity: 1})
            
            return () => timeline.current.kill();
        };
    }, [currentCard]);

    useEffect(() => {
        if (currentCard && descriptionRef.current) {
            timeline.current = gsap.timeline({
                defaults: {
                    duration: .3,
                    ease: "power2.out",
                }
            })
                .to(descriptionRef.current, {scaleY: .95, scaleX: .8, opacity: 0, y: -20})
                .to(descriptionRef.current, {scale: 1, opacity: 1, y: 0})
            
            return () => timeline.current.kill();
        };
    }, [currentCard]);

    return (
        <div className="altar-wrapper">
            <DialogWindow 
                isOpen={isOpenInfo}
                setIsOpen={setIsOpenInfo}
                isRendering={isRenderingInfo}
                setIsRendering={setIsRenderingInfo}
                children={<Weights />}
                description="Шансы выпадения карт"
            />

            <div className="altar-content">
                <div className="altar-card">
                    {(showCard) ? <div className="card" ref={cardRef}>
                        <Card card={showCard.card} />
                        <div className="card-border" />
                        <div className="card-border-blur" />
                    </div> :
                    <div className="card-sticker" ref={cardRef}><TgSticker src={STICKERS.cards} loop={false} /></div>}

                    {showCard && <div className="altar-card-description" ref={descriptionRef}>
                        <span className="name">{showCard.card.name} {showCard.is_repeat && "[повторка]"}</span>
                        <div className="bonus">
                            <div className="block">
                                <img className="icon" src={IMAGES.pts} alt="pts" />
                                <span className="vl">+{showCard.card.price} pts</span>
                            </div>

                            {showCard.reward > 0 && <div className="block">
                                <img className="icon" src={IMAGES.shards} alt="shards" />
                                <span className="vl">+{showCard.reward}</span>
                            </div>}

                            {showCard.is_repeat && <div className="block">
                                <img className="icon" src={getCardImage(showCard.card.rare)} alt="repeat" />
                                <span className="vl">+{1}</span>
                            </div>}
                        </div>
                    </div>}

                    <div className="altar-button"><Button description="Получить карту" onClick={onClickGetCard} /></div>
                </div>

                <div className="altar-info" onClick={() => setIsOpenInfo(true)}>
                    <InfoSvg />
                </div>
            </div>

            {currentCard && <CardInfo 
                card={currentCard.card}
                isRepeat={currentCard.is_repeat}
                reward={currentCard.reward}
            />}
        </div>
    );
};

export default AltarCard;